/**
 * Module dependencies.
 */

import { CollectionMediaNameKeys as CollectionName, CollectionProps, MediaProps } from 'src/types/collection';
import { ProductProps } from 'src/types/products';
import { absoluteUrlResolver } from 'src/core/utils/url-resolver';
import { color } from 'src/styles/utils';
import { ifNotProp } from 'styled-tools';
import { mediasCollectionName } from 'src/core/collections-config/media';
import { normalizeColors } from 'src/core/utils/colors';
import { routeTemplate } from 'src/core/utils/routes';
import { routes } from 'src/core/routes';
import { useRouter } from 'next/router';
import { useTranslate } from 'src/core/utils/translator';
import DownloadsSection, { Item as DownloadItem } from 'src/components/sections/downloads-section';
import Effect from 'src/components/collections/collection-details/effect';
import FavoritesProvider from 'src/context/favorites/provider';
import FiltersListProvider from 'src/context/filters-list/provider';
import Header from 'src/components/collections/collection-details/header';
import ImagesGallery from 'src/components/sections/images-gallery';
import Loading from 'src/components/core/loading';
import Metatags from 'src/components/metatags';
import ProductsListSection from 'src/components/collections/collection-details/products-list-section';
import React, { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import RelatedGallery from 'src/components/sections/related-gallery';
import SpecialPiecesSection from 'src/components/collections/collection-details/special-pieces-section';
import SpecsSection from 'src/components/collections/collection-details/specs-section';
import compact from 'lodash/compact';
import concat from 'lodash/concat';
import filter from 'lodash/filter';
import get from 'lodash/get';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import pick from 'lodash/pick';
import size from 'lodash/size';
import sortBy from 'lodash/sortBy';
import styled, { createGlobalStyle, css } from 'styled-components';
import uniqBy from 'lodash/uniqBy';
import useBreakpoint from 'src/hooks/use-breakpoint';
import useCollection from 'src/api/app/collections/use-collection';
import useNavbarTheme from 'src/context/navbar-theme/use-navbar-theme';

/**
 * Default locale.
 */

const defaultLocale: string = process.env.NEXT_PUBLIC_DEFAULT_LOCALE;

/**
 * Export `Props` type.
 */

export type Props = {
  data: CollectionProps & {
    related: CollectionProps[];
  };
  isKiosk?: boolean;
  products: {
    pageParams: number[];
    pages: Array<{
      data: ProductProps[];
      nextPage: number | null;
      total: number;
    }>;
  };
};

/**
 * `GlobalStyle` component.
 */

const GlobalStyle = createGlobalStyle`
  body {
    background-color: ${color('beige100')};
  }
`;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  min-height: 100vh;
  position: relative;
`;

/**
 * `StyledRelatedGallery` styled component.
 */

const StyledRelatedGallery = styled(RelatedGallery)<{ hasPadding: boolean }>`
  ${ifNotProp(
    'hasPadding',
    css`
      padding-top: 0 !important;
    `
  )}
`;

/**
 * Get images by collection name.
 */

function getImagesByCollectionName(medias: MediaProps[], name: CollectionName): MediaProps[] | null {
  return filter(medias, { collectionName: mediasCollectionName[name] });
}

/**
 * Get image by collection name.
 */

function getImageByCollectionName(medias: MediaProps[], name: CollectionName): string | null {
  const media = head(getImagesByCollectionName(medias, name));

  return media?.fileUrl;
}

/**
 * `CollectionDetailsPage` component.
 */

const CollectionDetailsPage = (props: Props): ReactElement => {
  const { isKiosk } = props;
  const { locales, query } = useRouter();
  const { translate } = useTranslate();
  const { data, isLoading, isSuccess } = useCollection({
    initialData: props.data,
    slug: get(query, 'slug')
  });

  const medias = useMemo(() => data?.media ?? [], [data?.media]);
  const headerGallery = useMemo(() => {
    const images = getImagesByCollectionName(medias, 'featuredImages');
    const videoImage = getImageByCollectionName(medias, 'videoImage');
    const videoUrl = data?.videoUrl;

    return compact(
      concat(
        videoImage && videoUrl
          ? {
              coverImage: videoImage,
              type: 'video',
              url: videoUrl
            }
          : null,
        map(images, ({ customProperties, fileUrl }) => ({
          label: customProperties?.title,
          type: 'image',
          url: fileUrl
        }))
      )
    );
  }, [data, medias]);

  const imagesGallery = useMemo(() => {
    const images = getImagesByCollectionName(medias, 'gallery');

    return map(images, ({ customProperties, fileUrl, id }) => ({
      id,
      title: customProperties?.title || data?.name,
      url: fileUrl
    }));
  }, [data?.name, medias]);

  const { colors, colorsTotal } = useMemo(() => {
    const images = getImagesByCollectionName(medias, 'colorImage');
    const colorImages = sortBy(images, ['orderColumn']);
    const normalizedColors = normalizeColors(data?.colors, colorImages);
    const total = size(normalizedColors);

    return {
      colors: normalizedColors,
      colorsTotal: total > 0 ? total : data?.colors.length
    };
  }, [data, medias]);

  const { formats, formatsTotal } = useMemo(() => {
    const list = uniqBy(data?.formats, 'name');

    return {
      formats: list,
      formatsTotal: list?.length ?? 0
    };
  }, [data]);

  const technicalInformation: DownloadItem[] = useMemo(
    () => [
      {
        downloadUrl: data?.catalogDownload?.downloadUrl,
        type: 'catalog'
      },
      {
        downloadUrl: data?.technicalInformationDownload?.downloadUrl,
        type: 'technicalInformation'
      },
      {
        downloadUrl: data?.technicalManualDownload?.downloadUrl,
        type: 'technicalManual'
      },
      {
        downloadUrl: getImageByCollectionName(medias, 'imagesArchive'),
        type: 'images'
      },
      {
        downloadUrl: getImageByCollectionName(medias, 'bimArchive'),
        type: 'bim'
      }
    ],
    [data, medias]
  );

  const isMobile = useBreakpoint('ms', 'max');
  const technicalInformationSectionRef = useRef<HTMLDivElement>(null);
  const handleGotoTechnicalInformationSection = useCallback(() => {
    if (technicalInformationSectionRef) {
      technicalInformationSectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: isMobile ? 'start' : 'nearest'
      });
    }
  }, [isMobile]);

  const technicalInformationDownloads = filter(technicalInformation, ({ downloadUrl }) => {
    return !isEmpty(downloadUrl);
  });

  const [forceUpdate, setForceUpdate] = useState<boolean>();
  const relatedGalleryHasPadding = useMemo(() => {
    const hasDownloadSection = size(technicalInformationDownloads) > 0;
    const hasSpecialPiecesSection = size(data?.specialPieces) > 0;

    return hasDownloadSection || hasSpecialPiecesSection;
  }, [data, technicalInformationDownloads]);

  const hrefLangs = useMemo(() => {
    return locales.map((locale) => {
      const route = routeTemplate(routes[locale].collection, { slug: data?.slug });

      return {
        lang: locale,
        url: defaultLocale === locale ? absoluteUrlResolver(`${route}`) : absoluteUrlResolver(`/${locale}${route}`)
      };
    });
  }, [data?.slug, locales]);

  useNavbarTheme({ theme: 'dark' });

  return (
    <>
      <Metatags
        description={data?.descriptionSeo}
        hrefLangs={hrefLangs}
        isKiosk={isKiosk}
        pageTitle={data?.name}
        title={data?.titleSeo}
      />

      <GlobalStyle />

      <Wrapper>
        <Loading active={isLoading} />

        {isSuccess && (
          <>
            <FavoritesProvider>
              <Header
                colorsTotal={colorsTotal}
                finishesTotal={data?.finishes.length}
                formatsTotal={formatsTotal}
                gallery={headerGallery}
                imageUrl={getImageByCollectionName(medias, 'imageCloseup')}
                isKiosk={isKiosk}
                isNew={data?.new}
                media={data?.media}
                name={data?.name}
                subTitle={data?.subTitle}
                typologies={data?.typologies}
              />

              <Effect
                description={data?.secondaryDescription}
                hasTechnicalInformationDownloads={!isEmpty(technicalInformationDownloads)}
                imageLabel={data?.secondaryImageDescription}
                imageUrl={getImageByCollectionName(medias, 'secondaryImage')}
                isKiosk={isKiosk}
                onGotoTechnicalInformationSection={handleGotoTechnicalInformationSection}
                title={data?.secondaryTitle}
              />

              <ImagesGallery isKiosk={isKiosk} items={imagesGallery} label={translate('common:labels.gallery')} />

              <SpecsSection
                {...pick(data, ['finishes', 'formats', 'functions'])}
                colors={colors}
                functionImages={filter(medias, { collectionName: mediasCollectionName.functionImage })}
                isKiosk={isKiosk}
              />

              <FiltersListProvider omitUrl>
                <ProductsListSection
                  collectionId={data?.id}
                  isKiosk={isKiosk}
                  onForceUpdate={setForceUpdate}
                  productColors={data?.colors}
                  productFinishes={data?.finishes}
                  productFormats={formats}
                  productFunctions={data?.functions}
                  productThickness={data?.thicknesses}
                  productTypologies={data?.typologies}
                  products={props.products}
                />
              </FiltersListProvider>

              <div key={String(forceUpdate)}>
                <SpecialPiecesSection
                  description={data?.descriptionSpecialPieces}
                  isKiosk={isKiosk}
                  specialPieces={data?.specialPieces}
                />

                {!isKiosk && (
                  <DownloadsSection
                    description={
                      data?.descriptionTechnicalInformation ??
                      translate('common:sections.technicalInformation.description')
                    }
                    items={technicalInformationDownloads}
                    ref={technicalInformationSectionRef}
                    title={translate('common:sections.technicalInformation.title')}
                  />
                )}

                <StyledRelatedGallery
                  hasPadding={relatedGalleryHasPadding}
                  isKiosk={isKiosk}
                  items={data?.related}
                  label={translate('collections:details.related.label')}
                  type={'collection'}
                />
              </div>
            </FavoritesProvider>
          </>
        )}
      </Wrapper>
    </>
  );
};

/**
 * Export `CollectionDetailsPage` component.
 */

export default CollectionDetailsPage;
